import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface StageSkipConfirmationModalProps {
  open: boolean;
  nextStageName: string;
  isSubmitting?: boolean;
  onConfirm: (markComplete: boolean) => void;
}

export function StageSkipConfirmationModal({
  open,
  nextStageName,
  isSubmitting = false,
  onConfirm,
}: Readonly<StageSkipConfirmationModalProps>) {
  const [selection, setSelection] = useState<'yes' | 'no'>('no');

  const handleConfirm = () => {
    onConfirm(selection === 'yes');
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-md [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Mark next stage as complete?</DialogTitle>
          <DialogDescription>
            The next scheduled stage on this collection strategy is <strong>{nextStageName}</strong>. Do you want to
            mark it as complete and skip it? Choose "No" if this email was not part of chasing (e.g. a copy invoice or
            query response).
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          className="py-2"
          value={selection}
          onValueChange={(value) => setSelection(value === 'yes' ? 'yes' : 'no')}
        >
          <RadioGroupItem value="no" id="stage-skip-no" disabled={isSubmitting}>
            No — keep the stage; automation continues as scheduled
          </RadioGroupItem>
          <RadioGroupItem value="yes" id="stage-skip-yes" disabled={isSubmitting}>
            Yes — mark {nextStageName} as complete and skip it
          </RadioGroupItem>
        </RadioGroup>

        <DialogFooter>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
